import React, { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { getBase64StringFromDataURL } from "../../../../../util/Scripts";
import { changeWorkflowImage } from "../../../../../services/api/Api";
import { Message, Upload, Button } from "../../../../BP2Components";
import { useIntl } from "react-intl";
import { Icon_Delete, Icon_Edit, Icon_Upload } from "../../../../BP2Components/Icons/icons";
//@ts-ignore
import projectConfig from "@projectConfig";
import "./CaseImage.less";
import clsx from "clsx";
import ShowDate from "../ShowDate/ShowDate";

interface CaseImageProps {
   instanceId: string;
   defaultImageUrl: string;
   configId?: string | null;
   imageShadow?: boolean;
   className?: string;
   children?: ReactNode;
   deadlineDate?: Date | string | null;
}

const CaseImage: React.FC<CaseImageProps> = ({
   instanceId,
   defaultImageUrl = "",
   configId = null,
   imageShadow = false,
   className,
   children,
   deadlineDate,
}) => {
   const [backgroundImage, setBackgroundImage] = useState(defaultImageUrl);
   const intl = useIntl();
   const role = useSelector((state: any) => state.boards.role);
   const { areWidgetSettingsVisible } = useSelector((state: any) => state.settings);
   const isOwner = role === "OWNER";

   const validateImageFile = (file: File): boolean => {
      if (!file.type.startsWith("image/")) {
         Message.error(intl.formatMessage({ id: "forms.wrongFileType" }));
         return false;
      }
      return true;
   };

   const convertFileToBase64 = (file: File): Promise<string | null> => {
      return new Promise((resolve) => {
         const reader = new FileReader();
         reader.onload = (event) => {
            const base64 = getBase64StringFromDataURL(event.target?.result);
            resolve(base64);
         };
         reader.onerror = () => resolve(null);
         reader.readAsDataURL(file);
      });
   };

   const uploadImage = async (
      file: File,
      targetInstanceId: string | undefined,
      targetConfigId: string | null | undefined,
      successMessageId: string,
   ) => {
      if (!validateImageFile(file)) return;

      const base64 = await convertFileToBase64(file);
      if (!base64) {
         Message.error(intl.formatMessage({ id: "forms.uploadFailed" }));
         return;
      }

      const cleanedType = file.type.replace(/^image\//, "");
      Message.loading(intl.formatMessage({ id: "global.loading" }));

      try {
         const response = await changeWorkflowImage(
            targetInstanceId,
            file.name,
            cleanedType,
            base64,
            targetConfigId,
         );

         setBackgroundImage(response.data.changeDefaultLogo.url.url);
         Message.success(intl.formatMessage({ id: successMessageId }));
      } catch (error) {
         console.error("Upload error:", error);
         Message.error(intl.formatMessage({ id: "forms.uploadFailed" }));
      } finally {
         Message.destroy();
      }
   };

   const handleUploadImage = (file: File, isConfigUpload: boolean = false) => {
      if (isConfigUpload) {
         if (!configId) {
            Message.warning(intl.formatMessage({ id: "forms.noConfigId" }));
            return;
         }
         uploadImage(file, undefined, configId, "forms.uploadConfigSucces");
      } else {
         uploadImage(file, instanceId, undefined, "forms.uploadSucces");
      }
   };

   const handleDeleteBackgroundImage = async () => {
      Message.loading(intl.formatMessage({ id: "global.loading" }));

      try {
         await changeWorkflowImage(instanceId, "", "", "", undefined);
         setBackgroundImage("");
         Message.success(intl.formatMessage({ id: "forms.deleteSucces" }));
      } catch (error) {
         console.error("Delete error:", error);
         Message.error(intl.formatMessage({ id: "forms.deleteFailed" }));
      } finally {
         Message.destroy();
      }
   };

   const getCaseImageClassName = () => {
      let fullClassName = "case-image";
      if (!backgroundImage) {
         fullClassName += " no-image";
      }
      if (imageShadow) {
         fullClassName += " shadow";
      }
      if (className) {
         fullClassName += ` ${className}`;
      }
      return fullClassName;
   };

   return (
      <div
         style={{
            backgroundImage: `url(${backgroundImage})`,
         }}
         className={getCaseImageClassName()}
      >
         {(!projectConfig?.hideProcessImageButtons || deadlineDate) && (
            <div className="case-image__overlay">
               {!projectConfig?.hideProcessImageButtons && (
                  <div
                     className={clsx(
                        "case-image__buttons",
                        backgroundImage ? "case-image__buttons--margin" : "",
                     )}
                  >
                     <Upload
                        onUpload={(file) => handleUploadImage(file, false)}
                        uploadUrl={""}
                        className="case-image__upload"
                     >
                        <Button buttonType="primary" className="case-image__button upload-button">
                           <Icon_Edit />
                        </Button>
                     </Upload>
                     {backgroundImage && (
                        <Button
                           buttonType="danger"
                           onClick={handleDeleteBackgroundImage}
                           className="case-image__button delete-button"
                        >
                           <Icon_Delete />
                        </Button>
                     )}
                     {isOwner && configId && areWidgetSettingsVisible && (
                        <Upload
                           onUpload={(file) => handleUploadImage(file, true)}
                           uploadUrl={""}
                           className="case-image__upload"
                        >
                           <Button
                              buttonType="secondary"
                              className="case-image__button config-button"
                              title={intl.formatMessage({ id: "forms.uploadConfigImage" })}
                           >
                              <Icon_Upload />
                           </Button>
                        </Upload>
                     )}
                  </div>
               )}
               {deadlineDate && (
                  <ShowDate className="case-image__date" date={deadlineDate} dateType="deadline" />
               )}
            </div>
         )}
         <div
            className={clsx(
               "case-image__content",
               !backgroundImage ? "case-image__content--no-padding" : "",
            )}
         >
            {children}
         </div>
      </div>
   );
};

export default CaseImage;
